import React from "react";
import {
  CheckBox,
  DynamicDropdown,
  FileDownloader,
  StaticDropdown,
} from "src/Components";
import TableTemplate, { ITableUI } from "src/Tables/TableTemplate/tableTemplate";
import ImbalanceSummaryTableUI from "./ImbalanceSummaryTableUI";
import ImbalanceCoveredCallPortfolioTableUI from "./ImbalanceCoveredCallPortfolioTableUI";
import { getPortfolioGroupInstrumentOptions } from "../portfolioGroupInstrumentOptions";
import {
  getPortfoImbalanceSummaryTable,
  getPortfoImbalanceSummaryExcel,
  getCoveredCallPortfolioTable,
} from "src/Requests/portfolioGroups";
import type {
  ICoveredCallPortfolioTableReport,
  IPortfoImbalanceSummarySearchParams,
  IPortfoImbalanceSummaryTableReport,
  IStrategyPortfolioSearchParams,
} from "src/Interfaces/portfolioGroups";
import { Header } from "src/Interfaces/TableInterfaces";
import { portfolioGroupExchangeTypeOptions } from "src/Utils/exchangeTypeOptions";
import PortfolioFilterMultiSelects from "../PortfolioFilterMultiSelects";

const ImbalanceSummary = () => {
  const [showHiddenCols, setShowHiddenCols] = React.useState(false);
  const [selectedCell, setSelectedCell] = React.useState<{
    accountId: number;
    accountName: string;
    baseInstrumentId: string;
    baseAsset: string;
  } | null>(null);
  const selectedCellRef = React.useRef(selectedCell);
  selectedCellRef.current = selectedCell;
  const [searchParams, setSearchParams] =
    React.useState<IPortfoImbalanceSummarySearchParams>({
      portfoGroupIds: [],
      accountIds: [],
      instrumentId: null,
      exchangeType: null,
    });

  const filterChangeHandler = (name: string, value: number | string) => {
    setSearchParams((prev) => ({
      ...prev,
      [name]:
        value === "" || value == null
          ? null
          : name === "instrumentId"
            ? String(value)
            : Number(value),
    }));
    setSelectedCell(null);
  };

  const multiSelectFilterChangeHandler = (
    name: "portfoGroupIds" | "accountIds",
    value: string[],
  ) => {
    setSearchParams((prev) => ({ ...prev, [name]: value.map(Number) }));
    setSelectedCell(null);
  };

  const summaryCellClickHandler = React.useCallback((
    row: IPortfoImbalanceSummaryTableReport,
    header: Header,
  ) => {
    if (row.id <= 0 || !header.groupName || !header.tag) return;

    const baseInstrumentId = String(header.tag);
    setSelectedCell((previous) =>
      previous?.accountId === row.id &&
      previous.baseInstrumentId === baseInstrumentId
        ? null
        : {
            accountId: row.id,
            accountName: row.accountName ?? "-",
            baseInstrumentId,
            baseAsset: header.groupName!,
          },
    );
  }, []);

  const SummaryTableUI = React.useMemo(
    () =>
      ({ reports, onAction, sortingType, setSortingType }: ITableUI<IPortfoImbalanceSummaryTableReport>) => (
        <ImbalanceSummaryTableUI
          reports={reports}
          onAction={onAction}
          sortingType={sortingType}
          setSortingType={setSortingType}
          showHiddenCols={showHiddenCols}
          onCellClick={summaryCellClickHandler}
          isCellActive={(row, header) =>
            selectedCellRef.current?.accountId === row.id &&
            selectedCellRef.current.baseInstrumentId === String(header.tag ?? "")
          }
          tableBoxClassName="h-[calc(75vh-10.5rem)] min-h-[360px] portfolio-group-first-row portfolio-summary-thick-top-border portfolio-summary-reduced-top-border"
        />
      ),
    [showHiddenCols, summaryCellClickHandler],
  );

  return (
    <div className="flex flex-col flex-1 w-full gap-3">
      <div className="flex flex-row justify-between items-end gap-3">
        <div className="flex flex-row justify-start items-end gap-3">
          <PortfolioFilterMultiSelects
            portfoGroupIds={searchParams.portfoGroupIds ?? []}
            accountIds={searchParams.accountIds ?? []}
            onChange={multiSelectFilterChangeHandler}
          />
          <div className="w-[220px]">
            <StaticDropdown
              dropdown={{
                name: "exchangeType",
                placeholder: "بورس",
                extraLabel: true,
              }}
              options={portfolioGroupExchangeTypeOptions}
              onChange={filterChangeHandler}
              value={searchParams.exchangeType?.toString() ?? ""}
            />
          </div>
          <div className="w-[220px]">
            <DynamicDropdown
              dropdown={{
                name: "instrumentId",
                placeholder: "نماد پایه",
                extraLabel: true,
              }}
              getData={getPortfolioGroupInstrumentOptions}
              onChange={filterChangeHandler}
              value={searchParams.instrumentId ?? ""}
            />
          </div>
          <div className="w-36">
            <FileDownloader getFile={() => getPortfoImbalanceSummaryExcel(searchParams)} />
          </div>
        </div>
      </div>
      <div className="min-h-8 flex items-center mb-2">
        <CheckBox
          checked={showHiddenCols}
          label="نمایش جزییات"
          name="showHiddenCols"
          onChange={(_, checked) => setShowHiddenCols(checked)}
        />
      </div>
      <TableTemplate<
        IPortfoImbalanceSummarySearchParams,
        IPortfoImbalanceSummaryTableReport
      >
        getDataTable={getPortfoImbalanceSummaryTable}
        searchParameters={searchParams}
        TableUI={SummaryTableUI}
        refreshTableTime={30000}
        pagesize={200}
        withSort
        tableWrapperClassname="!mx-0 flex justify-center"
      />
      {selectedCell ? (
        <div className="w-full flex flex-col items-center gap-2">
          <div className="text-center text-sm font-bold">
            {selectedCell.accountName} - {selectedCell.baseAsset}
          </div>
          <TableTemplate<
            IStrategyPortfolioSearchParams,
            ICoveredCallPortfolioTableReport
          >
            key={`${selectedCell.accountId}-${selectedCell.baseInstrumentId}`}
            getDataTable={getCoveredCallPortfolioTable}
            searchParameters={{
              accountId: selectedCell.accountId,
              baseInstrumentId: selectedCell.baseInstrumentId,
            }}
            TableUI={(props) => <ImbalanceCoveredCallPortfolioTableUI {...props} />}
            withSort
            pagesize={50}
            tableWrapperClassname="!mx-0"
          />
        </div>
      ) : null}
    </div>
  );
};

export default ImbalanceSummary;
